import React, { MouseEventHandler, useCallback, useEffect, useId, useMemo, useRef } from "react";
import {
	DataGridProProps,
	FilterPanelState,
	GridApiPro,
	gridPreferencePanelStateSelector,
	GridPreferencePanelsValue,
	GridRow,
	GridRowClassNameParams,
	GridRowHeightReturnValue,
	GridRowId,
	GridRowParams,
	GridRowProps,
	GridRowSelectionModel,
	useGridApiContext,
	useGridApiRef,
	useGridSelector,
} from "@mui/x-data-grid-pro";
import { StyledDataGrid } from "src/components/common/dataGrid/styles/StyledDataGrid.tsx";
import { useContextMenu } from "src/components/common/contextMenu/useContextMenu.ts";
import { AavoContextMenu } from "../contextMenu/AavoContextMenu";
import {
	flattenAavoDataGridColumns,
	getDataGridRowsByIds,
	mergeRowClassNames,
} from "src/components/common/dataGrid/utils.ts";
import { Badge, Box, Tooltip, useTheme } from "@mui/material";
import { mergeSx } from "src/utils/styles.ts";
import { faColumns3, faExternalLink, faFilter, faRefresh } from "@fortawesome/pro-regular-svg-icons";
import { AavoDataGridApi } from "src/components/common/dataGrid/AavoDataGridApi.ts";
import { useMaybeControlledState } from "src/utils/useMaybeControlledState.ts";
import { AavoDataGridContext } from "./AavoDataGridContext";
import { AavoActionBar } from "src/components/common/actionBar/AavoActionBar.tsx";
import { AsyncButton } from "src/components/common/buttons/AsyncButton.tsx";
import { useContextOrThrow } from "src/utils/useContextOrThrow.tsx";
import { HorizontalBox } from "../box/HorizontalBox.tsx";
import { actionBarSxContentOnly } from "../actionBar/actionBarStyles.ts";
import { AavoMenuButton } from "../menu/AavoMenuButton.tsx";
import { VerticalBox } from "../box/VerticalBox.tsx";
import { isTouchDevice } from "src/utils/isTouchDevice.ts";
import { AsyncMenuButton } from "../contextMenu/AsyncMenuButton.tsx";
import i18n from "i18next";
import { useDataGridColumnStateSaving } from "src/components/common/dataGrid/useDataGridColumnStateSaving.ts";
import { FilterPanelTrigger } from "@mui/x-data-grid";
import { AavoButton } from "src/components/common/buttons/AavoButton.tsx";
import type { GridSlotProps } from "@mui/x-data-grid/models";
import { useIsMobile } from "src/utils/breakpointUtils.ts";
import {
	AavoDataGridMobileViewConfig,
	AavoDataGridRowContextMenuParams,
	AavoGridColDef,
	FlattenedAavoGridColDef,
} from "src/components/common/dataGrid/types.tsx";
import { AavoDataGridMobileViewRow } from "src/components/common/dataGrid/mobileView/AavoDataGridMobileViewRow.tsx";
import { getInitialStateWithMobileColumnDefaults } from "src/components/common/dataGrid/mobileView/mobileColumnVisibilityDefaults.ts";
import { getFontSizeSetting } from "../../../localSettings/utils.ts";
import type { AgentDataGridContext } from "src/components/views/ai/assistant/uiContext/agentUiNodeTypes.ts";
import { AgentDataGridUiContext } from "src/components/views/ai/assistant/uiContext/AgentDataGridUiContext.tsx";
import type {
	CustomFunctionUiActionView,
} from "src/api/generated/io/aavo/applications/db/erp/types/customFunctionUiActionView.ts";
import {
	CustomUiActionObjectMenuItems,
} from "src/components/common/customUiActions/CustomUiActionMenuItems.tsx";
import {
	resolveCustomUiActionObjectIdsForView,
} from "src/components/common/customUiActions/customUiActionDataGrid.ts";

export interface AavoDataGridProps<RowData extends object>
	extends
		Omit<
			DataGridProProps<RowData>,
			| "onRowSelectionModelChange"
			| "rowSelectionModel"
			| "initialState"
			| "getRowClassName"
			| "columns"
			| "apiRef"
			| "getRowId"
			| "listView" // Replaced with mobileView
			| "listViewColumn"
		>,
		Required<Pick<DataGridProProps<RowData>, "initialState" | "getRowId">> {
	columns: AavoGridColDef<RowData>[];
	selectedRows?: GridRowId[];
	onRowSelectionChanged?: (selectedRowIds: GridRowId[]) => void;
	refreshData?: () => Promise<unknown>;
	hideRefreshButton?: boolean;
	actionBarComponents?: React.ReactNode;
	actionBarMenuComponents?: React.ReactNode;
	actionBarMenuComponents2?: React.ReactNode; // A bit hacky way to make data grid wrapper hooks able to add default menu components.
	openRow?: (row: RowData) => Promise<unknown> | unknown;
	rowContextMenuComponents?: (
		params: AavoDataGridRowContextMenuParams<RowData>,
	) => React.ReactNode | Promise<React.ReactNode>;
	customFunctionUiActionView?: CustomFunctionUiActionView;
	customFunctionUiActionObjectId?: (row: RowData) => number | null;
	getRowClassNames?: (params: GridRowClassNameParams<RowData>) => string[] | string | undefined;
	getRowTooltip?: (row: RowData) => string | undefined;
	apiRef?: React.RefObject<AavoDataGridApi<RowData> | null>;
	actionBarMenuButtonRef?: React.Ref<HTMLButtonElement>;
	checkboxSelectOnTouchDevice?: boolean;
	disableColumnSorting?: boolean;
	disableColumnOrderPersist?: boolean;
	hideActionBar?: boolean;
	listViewOnMobile?: boolean | AavoDataGridMobileViewConfig<RowData>;
	/** Rows grow to fit their content, e.g. wrapped long text, instead of having one fixed height. */
	autoRowHeight?: boolean;
	gridId: string;
	agentContext: AgentDataGridContext<RowData>;
}

export const AavoDataGrid = <RowData extends object>(props: AavoDataGridProps<RowData>) => {
	const {
		apiRef: forwardedApiRef,
		selectedRows: selectedRowsProp,
		onRowSelectionChanged,
		hideFooter = true,
		rowContextMenuComponents,
		customFunctionUiActionView,
		customFunctionUiActionObjectId,
		slotProps,
		getRowClassNames,
		getRowTooltip,
		columns,
		getRowId,
		sx,
		refreshData,
		hideRefreshButton = false,
		actionBarComponents,
		actionBarMenuComponents,
		actionBarMenuComponents2,
		openRow,
		actionBarMenuButtonRef,
		slots,
		autosizeOnMount = false,
		// TODO try autosizing again with MUI Data Grid V8
		// Currently autosizing fails to disable column virtualization when called on mount with setTimeout
		autosizeOptions = {
			includeOutliers: true,
			includeHeaders: true,
		},
		checkboxSelection: checkboxSelectionProp,
		checkboxSelectOnTouchDevice = false,
		initialState,
		disableColumnSorting = false,
		onColumnOrderChange,
		onColumnWidthChange,
		disableColumnReorder = false,
		disableColumnOrderPersist = disableColumnReorder,
		getDetailPanelHeight: getDetailPanelHeightProp,
		hideActionBar,
		rowHeight: rowHeightProp,
		autoRowHeight = false,
		listViewOnMobile,
		gridId,
		rows,
		rowCount,
		paginationMode,
		onRowDoubleClick: onRowDoubleClickProp,
		onStateChange,
		filterModel,
		sortModel,
		agentContext,
		...other
	} = props;

	// See https://github.com/mui/mui-x/issues/16135
	const gridApiRef = useGridApiRef() as React.RefObject<GridApiPro>;
	const theme = useTheme();
	const isMobile = useIsMobile();

	const [openContextMenu, contextMenuState] = useContextMenu();

	const [selectedRowIds, setSelectedRowIds] = useMaybeControlledState<GridRowId[]>({
		controlledValue: selectedRowsProp,
		onChange: onRowSelectionChanged,
		defaultValue: [],
	});

	const checkboxSelection = checkboxSelectionProp === true || (checkboxSelectOnTouchDevice && isTouchDevice());
	const onlySelectedRow = useMemo(() => {
		if (selectedRowIds.length !== 1 || rows == null) return undefined;
		return rows.find((row) => getRowId(row) === selectedRowIds[0]);
	}, [getRowId, rows, selectedRowIds]);

	const onRowSelectionModelChange = useCallback(
		(rowSelectionModel: GridRowSelectionModel) => {
			const newSelectedRowIds = rowSelectionModelToSelectedRowIds(rowSelectionModel, rows, getRowId);
			if (!checkboxSelection && newSelectedRowIds.length === 0)
				// Prevent unselecting rows by clicking on them - only allow selection changes that result in at least one selected row
				// On checkbox selection, we don't want this, because user can unselect rows by unchecking the checkbox.
				return;
			setSelectedRowIds(newSelectedRowIds);
		},
		[rows, getRowId, setSelectedRowIds, checkboxSelection],
	);

	const getGridApi = useCallback(() => {
		const current = gridApiRef.current;
		if (current === null) throw new Error("GridApiRef has no value");

		return current;
	}, [gridApiRef]);

	const getRowClassName = useCallback(
		(params: GridRowClassNameParams): string => {
			let fromProps = getRowClassNames?.(params) ?? [];
			if (typeof fromProps === "string") fromProps = [fromProps];
			return mergeRowClassNames(...fromProps);
		},
		[getRowClassNames],
	);

	const effectiveRowContextMenuComponents = useCallback(
		async (params: AavoDataGridRowContextMenuParams<RowData>) => {
			const objectIds = resolveCustomUiActionObjectIdsForView(
				customFunctionUiActionView,
				params.allSelectedRows,
				getRowId,
				customFunctionUiActionObjectId,
			);
			return (
				<>
				{customFunctionUiActionView != null && objectIds != null && (
					<CustomUiActionObjectMenuItems
						view={customFunctionUiActionView}
						objectIds={objectIds}
					/>
				)}
				{await rowContextMenuComponents?.(params)}
				</>
			);
		},
		[customFunctionUiActionView, customFunctionUiActionObjectId, getRowId, rowContextMenuComponents],
	);

	const onContextMenu: MouseEventHandler<any> = useCallback(
		(event) => {
			if (
				event.type === "contextmenu" &&
				(rowContextMenuComponents !== undefined || customFunctionUiActionView != null)
			) {
				event.preventDefault();
				let rowId = event.currentTarget.getAttribute("data-id");
				const gridApi = getGridApi();
				if (rowId == null || gridApiRef.current === undefined) return;
				const row = gridApi.getRow(rowId);
				if (row == null) return;

				rowId = getRowId(row); // Set rowId again to ensure it has correct type.
				let newSelectedRowIds = selectedRowIds;
				const clickedRowIsSelected = selectedRowIds.includes(rowId);
				if (!clickedRowIsSelected) {
					newSelectedRowIds = [rowId];
					setSelectedRowIds(newSelectedRowIds);
				}
				const selectedRows = getDataGridRowsByIds<RowData>(getGridApi(), newSelectedRowIds);

				openContextMenu({
					content: () =>
						effectiveRowContextMenuComponents({
							row: row,
							allSelectedRows: selectedRows,
							multipleRowsSelected: selectedRows.length > 1,
							onlySingleRowSelected: selectedRows.length === 1,
						}),
					mouseEvent: event,
				});
			}
		},
		[
			rowContextMenuComponents,
			customFunctionUiActionView,
			effectiveRowContextMenuComponents,
			getGridApi,
			gridApiRef,
			selectedRowIds,
			openContextMenu,
			setSelectedRowIds,
			getRowId,
		],
	);

	const slotPropsWrapped: DataGridProProps["slotProps"] = useMemo(
		() => ({
			...slotProps,
			row: {
				...slotProps?.row,
				onContextMenu: onContextMenu,
			},
		}),
		[slotProps, onContextMenu],
	);

	const getRowModelsTyped = () => {
		const gridApi = getGridApi();
		return gridApi.getRowModels() as Map<GridRowId, RowData>;
	};

	useEffect(() => {
		if (forwardedApiRef)
			forwardedApiRef.current = {
				...gridApiRef.current,
				...forwardedApiRef.current,
				getRowModels: getRowModelsTyped,
				getRows: () => {
					const asMap = getRowModelsTyped();
					return Array.from(asMap.values());
				},
			};
	});

	const flattenColumns: FlattenedAavoGridColDef<RowData>[] = flattenAavoDataGridColumns(columns);
	const columnsMaybeWithoutSorting = disableColumnSorting ? disableSortingOnColumns(flattenColumns) : flattenColumns;
	const useListView = isMobile && listViewOnMobile != null && listViewOnMobile !== false;
	const initialStateWithMobileColumnDefaults = useMemo(
		() => getInitialStateWithMobileColumnDefaults(initialState, flattenColumns, useListView),
		[initialState, flattenColumns, useListView],
	);

	const allActionBarMenuComponents = [
		customFunctionUiActionView != null && (
			<CustomUiActionObjectMenuItems
				key={"customFunctionUiActions"}
				view={customFunctionUiActionView}
			/>
		),
		actionBarMenuComponents,
		actionBarMenuComponents2,
		<CommonMenuActionBarMenuComponents key={"common"} />,
	];

	const propsFromUseColumnStateSaving = useDataGridColumnStateSaving({
		columns: columnsMaybeWithoutSorting,
		initialState: initialStateWithMobileColumnDefaults,
		autosizeOptions: autosizeOptions,
		onColumnOrderChange: onColumnOrderChange,
		onColumnWidthChange: onColumnWidthChange,
		disableColumnOrderPersist: disableColumnOrderPersist,
	});

	const getDetailPanelHeight = useCallback(
		(params: GridRowParams) => {
			if (getDetailPanelHeightProp != null) return getDetailPanelHeightProp(params);
			return "auto";
		},
		[getDetailPanelHeightProp],
	);

	const getRowTooltipRef = useRef(getRowTooltip);
	useEffect(() => {
		getRowTooltipRef.current = getRowTooltip;
	}, [getRowTooltip]);

	const TooltipRowSlot = useMemo(() => makeTooltipRow((row: RowData) => getRowTooltipRef.current?.(row)), []);

	const getRowHeightMemoized = useCallback(
		() => getRowHeight(rowHeightProp, useListView || autoRowHeight),
		[rowHeightProp, useListView, autoRowHeight],
	);

	const onRowDoubleClick = useCallback<NonNullable<DataGridProProps<RowData>["onRowDoubleClick"]>>(
		async (params, event, details) => {
			if (openRow != null) {
				await openRow(params.row);
			} else {
				await onRowDoubleClickProp?.(params, event, details);
			}
		},
		[openRow, onRowDoubleClickProp],
	);

	return (
		<AgentDataGridUiContext
			agentContext={agentContext}
			gridId={gridId}
			selectedRowIds={selectedRowIds}
			onlySelectedRow={onlySelectedRow}
		>
			<VerticalBox
				sx={mergeSx({ flex: 1 }, autoRowHeight && { "& .MuiDataGrid-cell": { paddingY: 0.75 } }, sx)}
			>
				<AavoDataGridContext.Provider
					value={{
						refreshData,
						actionBarComponents,
						actionBarMenuButtonRef,
						actionBarMenuComponents: allActionBarMenuComponents,
						openRow: openRow == null ? undefined : (row) => openRow(row as RowData),
						onlySelectedRow,
						hideActionBar,
						hideRefreshButton,
					}}
				>
					<StyledDataGrid
						key={`${gridId}_${useListView ? "mobile" : "desktop"}`}
						apiRef={gridApiRef}
						pageSizeOptions={PAGE_SIZE_OPTIONS}
						rowSelectionModel={{
							type: "include",
							ids: new Set(selectedRowIds),
						}}
						onRowSelectionModelChange={onRowSelectionModelChange}
						rows={rows}
						hideFooter={hideFooter}
						slotProps={slotPropsWrapped}
						getRowClassName={getRowClassName}
						getRowId={(row: RowData) => getRowId(row)}
						getRowHeight={getRowHeightMemoized}
						columnHeaderHeight={theme.typography.fontSize * 1.85}
						autosizeOnMount={autosizeOnMount}
						initialState={initialStateWithMobileColumnDefaults}
						onStateChange={onStateChange}
						checkboxSelection={checkboxSelection}
						disableColumnReorder={disableColumnReorder}
						getDetailPanelHeight={getDetailPanelHeight}
						showToolbar
						listView={useListView}
						listViewColumn={{
							field: "__listViewCell__",
							renderCell: (params) => (
								<AavoDataGridMobileViewRow
									columns={flattenColumns}
									listViewOnMobile={listViewOnMobile}
									getRowId={getRowId}
									rowContextMenuComponents={effectiveRowContextMenuComponents}
									gridId={gridId}
									refreshData={refreshData}
									{...params}
								/>
							),
						}}
						slots={{
							toolbar: DataGridActionBar,
							row: TooltipRowSlot,
							...slots,
						}}
						rowCount={paginationMode === "server" ? rowCount : undefined}
						paginationMode={paginationMode}
						filterModel={filterModel}
						sortModel={sortModel}
						onRowDoubleClick={onRowDoubleClick}
						{...propsFromUseColumnStateSaving}
						{...other}
					/>
					<AavoContextMenu state={contextMenuState} />
				</AavoDataGridContext.Provider>
			</VerticalBox>
		</AgentDataGridUiContext>
	);
};

const DataGridActionBar = () => {
	const {
		refreshData,
		hideRefreshButton,
		actionBarComponents,
		actionBarMenuComponents,
		actionBarMenuButtonRef,
		openRow,
		onlySelectedRow,
		hideActionBar,
	} = useContextOrThrow(AavoDataGridContext);

	if (hideActionBar) return null;

	return (
		<HorizontalBox
			sx={{
				flexShrink: 0,
				paddingX: 0.4,
				paddingTop: 0.8,
				paddingBottom: 0.4,
			}}
		>
			<AavoActionBar
				type={"dataGrid"}
				sx={{
					padding: 0,
					flex: 1,
				}}
			>
				{refreshData && !hideRefreshButton && (
					<AsyncButton
						icon={faRefresh}
						color={"primary"}
						onClick={async () => {
							if (refreshData) await refreshData();
						}}
					/>
				)}
				<FilterPanelTrigger
					render={(buttonProps, state) => <OpenFilterButton buttonProps={buttonProps} state={state} />}
				/>
				{openRow != null && (
					<AsyncButton
						icon={faExternalLink}
						tooltip={i18n.t("open")}
						disabled={onlySelectedRow == null ? i18n.t("select_a_single_row") : undefined}
						onClick={async () => {
							if (onlySelectedRow == null) return;
							await openRow(onlySelectedRow);
						}}
					/>
				)}
				{actionBarComponents}
			</AavoActionBar>
			{actionBarMenuComponents != undefined && (
				<Box sx={actionBarSxContentOnly}>
					<AavoMenuButton buttonRef={actionBarMenuButtonRef} menuContent={actionBarMenuComponents} />
				</Box>
			)}
		</HorizontalBox>
	);
};

interface OpenFilterButtonProps {
	buttonProps: GridSlotProps["baseButton"];
	state: FilterPanelState;
}

const OpenFilterButton = ({ buttonProps, state }: OpenFilterButtonProps) => {
	return (
		<Box sx={{ position: "relative" }}>
			<AavoButton icon={faFilter} onClick={buttonProps.onClick} />
			{state.filterCount > 0 && (
				<Badge
					sx={{
						position: "absolute",
						bottom: 5,
						right: 8,
					}}
					color="secondary"
					variant="dot"
				/>
			)}
		</Box>
	);
};

const CommonMenuActionBarMenuComponents = () => {
	const apiRef = useGridApiContext();

	const columnButtonId = useId();
	const columnPanelId = useId();

	const preferencePanel = useGridSelector(apiRef, gridPreferencePanelStateSelector);

	return [
		<AsyncMenuButton
			key={"manageColumns"}
			id={columnButtonId}
			icon={faColumns3}
			label={i18n.t("columns")}
			onClick={() => {
				if (preferencePanel.open && preferencePanel.openedPanelValue === GridPreferencePanelsValue.columns) {
					apiRef.current.hidePreferences();
				} else {
					apiRef.current.showPreferences(GridPreferencePanelsValue.columns, columnPanelId, columnButtonId);
				}
			}}
		/>,
	];
};

function rowSelectionModelToSelectedRowIds<RowData extends object>(
	rowSelectionModel: GridRowSelectionModel,
	allRows: AavoDataGridProps<RowData>["rows"],
	getRowId: (row: RowData) => GridRowId,
): GridRowId[] {
	switch (rowSelectionModel.type) {
		case "include":
			return [...rowSelectionModel.ids];
		case "exclude":
			if (allRows == null) return [];
			return allRows.filter((row) => !rowSelectionModel.ids.has(getRowId(row))).map((row) => getRowId(row));
	}
}

function disableSortingOnColumns<RowData extends object>(
	columns: FlattenedAavoGridColDef<RowData>[],
): FlattenedAavoGridColDef<RowData>[] {
	return columns.map((column) => {
		return {
			...column,
			sortable: false,
		};
	});
}

function getRowHeight(explicitRowHeight: number | undefined, autoHeight: boolean): GridRowHeightReturnValue {
	if (autoHeight) {
		return "auto";
	} else if (explicitRowHeight != null) {
		return explicitRowHeight;
	} else {
		return getFontSizeSetting() * 2;
	}
}

const PAGE_SIZE_OPTIONS = [25, 50, 100, 150];

function makeTooltipRow<RowData extends object>(getRowTooltip: (row: RowData) => string | undefined) {
	return function TooltipRow(props: GridRowProps) {
		const tooltip = getRowTooltip(props.row as RowData);
		if (!tooltip) return <GridRow {...props} />;
		return (
			<Tooltip title={tooltip} placement={"bottom-start"} arrow>
				<GridRow {...props} />
			</Tooltip>
		);
	};
}
